import { PieChartLabelProps } from '@shared/models/charts/pie-chart.model.ts';

const RADIAN = Math.PI / 180;

export const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  index,
  percent,
  data,
  minLabelPercent,
  showCount,
  valueFormatter
}: PieChartLabelProps) => {
  if (percent < minLabelPercent) {
    return null;
  }

  // Place the label at the sector's area centroid (its true visual center)
  // rather than the radial midpoint — a slice's mass sits toward the outer
  // arc (area grows with r²), so narrow slices center further out.
  const alpha = percent * Math.PI; // half the sector's angular span, in radians
  const centroidRadius =
    alpha > 0
      ? ((2 / 3) *
          (outerRadius ** 3 - innerRadius ** 3) *
          (Math.sin(alpha) / alpha)) /
        (outerRadius ** 2 - innerRadius ** 2)
      : innerRadius + (outerRadius - innerRadius) * 0.5;
  // Nudge slightly outward from the strict area centroid for a more balanced
  // look. Enforce a minimum radius so a dominant slice's label never lands on
  // the center (where the white sector borders converge and hide the text),
  // and a maximum so it never reaches the outer arc.
  const radius = Math.min(
    Math.max(centroidRadius * 1.08, outerRadius * 0.3),
    outerRadius * 0.8
  );
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const item = data[index];
  const formattedValue = valueFormatter
    ? valueFormatter(item.value)
    : `$${item.value.toLocaleString()}`;

  return (
    <text
      x={x}
      y={y}
      fill="var(--white)"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontWeight: 500 }}
    >
      {showCount && (
        <tspan x={x} dy="-0.6em" fontSize={12}>
          {item.count}
        </tspan>
      )}
      <tspan x={x} dy={showCount ? '1.2em' : '0'} fontSize={12}>
        {formattedValue}
      </tspan>
    </text>
  );
};
